import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store';

import { ROUTE, STATS_FINANCIAL_HASH, STATS_MONEY_FLOW_HASH } from '../../routes';

import { PageShell, Tab, TabsRow } from '../../components';
import { SlideSwap, type NavDir } from '../../transitions';
import { STATS_TABS, statsTabOfPath, type StatsTab } from './tabs';
import PulseContent from '../pulse/PulseContent';
import WebsiteAnalyticsContent from '../website-analytics/WebsiteAnalyticsContent';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import type { HomePeriod, StatsTopTable } from '../../mock-data/types';
import { IntervalDropdown } from '../home/components';
import {
  STATS_GROUP_INTERVALS,
  initialStatsG4,
  nextStatsG4,
  selectSpotlight,
  type StatsLiveG4,
} from '../../mock-data/stats';
import {
  SectionHead,
  NewPlayers,
  FinancialPerformance,
  MoneyFlow,
  Earnings,
  OperationalKpis,
  Spotlight,
} from './components';
import { usePeriodPicker } from '../home/usePeriodPicker';
import { useStudioPeriod } from '../home/useStudioPeriod';
import { useStatsMetrics } from './useStatsMetrics';
import { useTopPlayers } from './useTopPlayers';
import { SectionAnchor, StatsPage, TwoCol } from './stats.styles';

export type StatsProps = {
  styleText?: string;
  className?: string;
};

export type { StatsTab } from './tabs';

// The studio side-menu glyphs, reused verbatim so the Stats tab carries the same icon the sidebar's Stats
// entry does (masked + tinted to the tab colour by the Tab component). Stats wears the pulse-line glyph and
// the Pulse tab the chart glyph (the files are named the other way round).
const MENU_ICON_CDN = 'https://micros-t.b-cdn.net';
const STATS_TAB_ICON = `${MENU_ICON_CDN}/studio-menu-icon-pulse-1779968968265.svg`;
const PULSE_TAB_ICON = `${MENU_ICON_CDN}/studio-menu-icon-ststs-1779968968264.svg`;

// A live slice tracks its current value and the value from before the last tick.
// Only the still-mock Spotlight uses this now; the wired sections come from the API.
type Slice<T> = { live: T; prev: T };

/** Drive a live slice on its own interval: each tick advances `live` and keeps the
 *  prior reading as `prev`. */
function useLiveSlice<T>(initial: T, advance: (prev: T) => T, intervalMs: number): Slice<T> {
  const [slice, setSlice] = React.useState<Slice<T>>({ live: initial, prev: initial });
  React.useEffect(() => {
    const id = window.setInterval(() => {
      setSlice((s) => ({ live: advance(s.live), prev: s.live }));
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [advance, intervalMs]);
  return slice;
}

/**
 * Stats — the brand / operational dashboard. A single global time range, pinned to
 * the (brand-named) metrics header, drives the Brand-metrics cards.
 *
 * Mounted as a fixed route at `/account/stats`, reusing the main layout chrome.
 * Data sourcing is MIXED (same pattern as Home):
 *  - New Players, Financial Performance, Money Flow, Earnings and the Operational
 *    Performance tiles are wired to the Owners Hub Stats API via {@link useStatsMetrics}
 *    (active brand + selected range drive one `daily_client_activity` request per
 *    range; changing the range re-requests and re-bases the deltas). Figures with
 *    no backing field yet (Player Balance) render `?`.
 *  - The Spotlight leaderboards are MIXED: Top Players is wired to the Stats API
 *    (`daily_player_activity`, top 5 by money wagered) via {@link useTopPlayers}.
 *    Top Games has no backing data set (the Stats API exposes no game dimension), so
 *    it stays MOCK and is HIDDEN by default, revealed only by the `show_in_progress`
 *    flag (see {@link useShowInProgress}).
 *
 * Brand switching mirrors Home: the page reads the lagged `displayBrand` from the
 * `casinoInstance` slice, so its content tracks the brand through the global
 * BrandTransition fade.
 */
const StatsContent = (): React.ReactElement => {
  const { t } = useTranslation();
  // Active brand — the lagged content brand (promotes under cover of the fade), so
  // the previous brand's figures stay on screen through a brand switch.
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  // Prefix the heading with the active brand's name (falls back to a plain
  // "Metrics" while a brand is still mid-setup / unnamed).
  const brandName = displayBrand?.casino_data?.details?.brand ?? '';

  // A single global time range drives every Brand-metrics card on the page. The
  // option set is shared with the Home Key Metrics header (same `HomePeriod`s, custom ranges included).
  // The range the owner last picked on any page with one (this page's default until then).
  const [activeInterval, setActiveInterval] = useStudioPeriod('7D');

  // Brand metrics + Operational Performance — from the Stats API, keyed on the
  // displayed brand + the selected range.
  const metrics = useStatsMetrics(displayBrand?.id, activeInterval);

  // Arrived through a section deep link (Home's Financial Performance / Money Flow cards): scroll that section into
  // view, two frames late so the tab's layout has settled.
  const { hash, key: locationKey } = useLocation();
  const financialRef = React.useRef<HTMLDivElement>(null);
  const moneyFlowRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const target = hash === STATS_FINANCIAL_HASH ? financialRef : hash === STATS_MONEY_FLOW_HASH ? moneyFlowRef : null;
    if (!target) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [hash, locationKey]);

  // Spotlight is MIXED now. Top Games is still MOCK: it re-fluctuates on its own
  // interval and fills in on a randomised first-load delay. The Stats API has no
  // game-level data set (verified live), so Top Games is HIDDEN by default and only
  // revealed by the `show_in_progress` flag. Swap `useMockEndpoint` for a real query
  // hook of the same `{ data, loading }` shape when a game-level endpoint lands, then
  // drop the gate.
  const showInProgress = useShowInProgress();
  const g4 = useLiveSlice<StatsLiveG4>(initialStatsG4, nextStatsG4, STATS_GROUP_INTERVALS.group4);
  const topGames = React.useMemo(() => selectSpotlight(g4.live), [g4]);
  const gamesEp = useMockEndpoint(topGames);

  // Top Players is wired to the API (`daily_player_activity`), keyed on the active
  // brand + the selected range: top 5 by money wagered (casino + sport stakes).
  const { table: topPlayers } = useTopPlayers(displayBrand?.id, activeInterval);

  // Players (real, always shown) plus Top Games only when the in-progress flag is on.
  // The renderer reads each board's `loading`, so they fill in independently.
  const spotlight = React.useMemo<StatsTopTable[]>(
    () =>
      showInProgress ? [{ ...topGames, loading: gamesEp.loading }, topPlayers] : [topPlayers],
    [showInProgress, topGames, gamesEp.loading, topPlayers],
  );

  // The range picker: the presets plus a custom run of days (shared with Home).
  const periodPicker = usePeriodPicker(activeInterval, setActiveInterval);

  return (
    <StatsPage>
        <SectionHead
          title={brandName ? t('{{brand}} Metrics') : t('Metrics')}
          placeholders={brandName ? { brand: brandName } : undefined}
          first
          sticky
          right={
            <IntervalDropdown
              value={activeInterval}
              options={periodPicker.options}
              onChange={setActiveInterval}
              custom={periodPicker.custom}
            />
          }
        />

        <NewPlayers data={metrics.newPlayers} interval={activeInterval} loading={metrics.loading} />
        <SectionAnchor ref={financialRef} id={STATS_FINANCIAL_HASH.slice(1)}>
          <FinancialPerformance data={metrics.financial} interval={activeInterval} loading={metrics.loading} />
        </SectionAnchor>

        <TwoCol>
          <SectionAnchor ref={moneyFlowRef} id={STATS_MONEY_FLOW_HASH.slice(1)}>
            <MoneyFlow data={metrics.moneyFlow} loading={metrics.loading} showInProgress={showInProgress} />
          </SectionAnchor>
          <Earnings data={metrics.earnings} loading={metrics.loading} />
        </TwoCol>

        <SectionHead title={t('Operational Performance')} />
        <OperationalKpis kpis={metrics.operational} loading={metrics.loading} />

        <SectionHead title={t('Spotlight')} />
        <Spotlight tables={spotlight} />
      </StatsPage>
  );
};

/**
 * Stats hosts the Stats dashboard, the Pulse dashboard and the Website (visitor) stats under three tabs.
 * Mounted at `/account/stats` (opens on Stats), `/account/pulse` (opens on Pulse) and
 * `/account/stats/website` (opens on Website). Each tab's body mounts only while active, so its data
 * hooks run only for the visible dashboard.
 */
const Stats = ({ styleText = '', className = '' }: StatsProps): React.ReactElement => {
  const { t } = useTranslation();
  // The active tab IS the route: /account/pulse shows Pulse, /account/stats shows Stats. Clicking a
  // tab navigates, so the URL always reflects the open dashboard (and the sidebar highlights Stats for
  // both routes — see SideMenu). Both routes render this same host.
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The Website tab shows only while its flag is on (generally available, kept as a rollback switch); off, its route
  // opens Stats.
  const websiteOn = useFeatureGate().isEnabled('website-analytics');
  const routeTab = statsTabOfPath(pathname);
  // The tab row stays put; only the open tab's content slides, the way the row reads (as the Website section does).
  // -1: not one of the Stats page's routes.
  const index = routeTab === null ? -1 : STATS_TABS.indexOf(routeTab === 'website' && !websiteOn ? 'stats' : routeTab);
  /** The tab on screen before this render: the slide's direction is read against it. */
  const shownRef = React.useRef(index === -1 ? 0 : index);
  const shown = shownRef.current;
  // Leaving the page, the outgoing copy re-renders under the next route (the transitions do not freeze it): it stays on
  // its last tab, so it neither slides another tab in nor resets the scroll the incoming page restores.
  const current = index === -1 ? shown : index;
  const tab: StatsTab = STATS_TABS[current];
  const direction: NavDir = current === shown ? 'none' : current > shown ? 'forward' : 'back';
  const contentRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (index === -1 || shownRef.current === index) return;
    shownRef.current = index;
    // A tab opens at the top of the page, as its own route did (the studio's scroller on desktop, the document on a
    // phone).
    const scroller = contentRef.current?.closest<HTMLElement>('.ai-bb-page-root');
    if (scroller) scroller.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [index]);

  const onTab = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      navigate(id === 'pulse' ? ROUTE.pulse() : id === 'website' ? ROUTE.statsWebsite() : ROUTE.stats());
    },
    [navigate],
  );

  return (
    <PageShell styleText={styleText} className={className}>
      <TabsRow role="tablist" aria-label={t('Stats')}>
        <Tab id="stats" iconSrc={STATS_TAB_ICON} label={t('Stats')} active={tab === 'stats'} onClick={onTab} />
        <Tab id="pulse" iconSrc={PULSE_TAB_ICON} label={t('Pulse')} active={tab === 'pulse'} onClick={onTab} />
        {websiteOn ? (
          <Tab id="website" icon="globe" label={t('Website analytics')} active={tab === 'website'} onClick={onTab} />
        ) : null}
      </TabsRow>
      <div ref={contentRef}>
        <SlideSwap token={tab} direction={direction}>
          {tab === 'stats' ? <StatsContent /> : tab === 'pulse' ? <PulseContent /> : <WebsiteAnalyticsContent />}
        </SlideSwap>
      </div>
    </PageShell>
  );
};

export default Stats;
